"use client";

import React, { useEffect, useState } from "react";
import Loader from "../common/Loader";
import IntegrationsCard from "./integrationsCard/IntegrationsCard";
import { dictionary } from "../../utils/dictionary";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";

import ModalDropiIntegration from "./Modals/ModalDropiIntegration";
import ModalMercadoPagoIntegration from "./Modals/ModalMercadoPagoIntegration";
import ModalWhatsAppIntegration from "./Modals/ModalWhatsAppIntegration";
import ModalMetaIntegration from "./Modals/ModalMetaIntegration";
import ModalSuccessIntegration from "./Modals/ModalSuccessIntegration";
import { INTEGRATIONS_UI_CONFIG } from "./config/integrations.config";

import { postDropiIntegration } from "../../services/integrations/integrationsService";
import { disconnectMetaIntegration } from "../../services/meta/metaService";
import { whatsappOAuthCallback } from "../../services/whatsapp/whatsappService";
import { EmbeddedSignupResult } from "../../hooks/useFacebookSDK";
import { decodeTokenService } from "../../services/decodeTokenService";
import {
  postRegisterSellerApi,
  registerSellerList,
  updatePaymentMethodActive,
  updatePaymentMethodInactive,
} from "../../services/payment/payment";

import { useIntegrations } from "./hooks/useIntegrations";
import { IntegrationType } from "./config/integrations.config";
import { handleApi } from "../../utils/handleApi";

const IntegrationsPanel: React.FC = () => {
  const { language, country } = useAuth();
  const { integrations: initialIntegrations, loading } = useIntegrations(country);
  const [integrations, setIntegrations] = useState<any[]>([]);

  useEffect(() => {
    setIntegrations(initialIntegrations);
  }, [initialIntegrations]);

  const [modalOpenDropi, setModalOpenDropi] = useState(false);
  const [modalOpenMercadoPago, setModalOpenMercadoPago] = useState(false);
  const [modalOpenWhatsApp, setModalOpenWhatsApp] = useState(false);
  const [modalOpenMeta, setModalOpenMeta] = useState(false);
  const [loadingWhatsApp, setLoadingWhatsApp] = useState(false);
  const [loadingMeta, setLoadingMeta] = useState(false);

  const [tokenDropi, setTokenDropi] = useState("");
  const [tokenMercadoPagoPublic, setTokenMercadoPagoPublic] = useState("");
  const [tokenMercadoPagoPrivate, setTokenMercadoPagoPrivate] = useState("");
  const [loadingMercadoPago, setLoadingMercadoPago] = useState(false);

  const [loadingCard, setLoadingCard] = useState(false);

  const [successModal, setSuccessModal] = useState<{ open: boolean; type: string | null }>({ open: false, type: null });

  const updateIntegrationState = (type: string, active: boolean) => {
    setIntegrations(prev =>
      prev.map(i =>
        i.type === type ? { ...i, isActive: active, isConnected: active } : i
      )
    );
  };

  const handleSuccessClose = () => {
    setSuccessModal({ open: false, type: null });
  };

  const showSuccess = (type: string) => {
    updateIntegrationState(type, true);
    setSuccessModal({ open: true, type });
  };

  const handleConnect = (type: IntegrationType) => {
    if (type === "dropi") setModalOpenDropi(true);
    if (type === "mercadopago") setModalOpenMercadoPago(true);
    if (type === "whatsapp") setModalOpenWhatsApp(true);
    if (type === "meta") setModalOpenMeta(true);
    if (type === "stripe") handlePostStripeIntegration(integrations.find((integration) => integration.type === "stripe")?.alreadyConfigured ?? false);
  };

  const handleCancel = async (type: IntegrationType) => {
    try {
      if (type === "dropi") {
        await postDropiIntegration({
          is_active: false,
          integration_token: "",
        });
      }

      if (type === "mercadopago") {
        await updatePaymentMethodInactive(registerSellerList.MERCADO_PAGO);
      }

      if (type === "stripe") {
        await updatePaymentMethodInactive(registerSellerList.STRIPE);
      }

      if (type === "meta") {
        await disconnectMetaIntegration();
      }

      toast.success(dictionary["integration-canceled"][language]);
      updateIntegrationState(type, false);
    } catch (error: any) {
      toast.error(
        dictionary["integration-error"][language] +
          ": " +
          dictionary[error?.response?.data?.trigger]?.[language]
      );
    }
  };

  const handlePostDropiIntegration = async () => {
    try {
      await postDropiIntegration({
        integration_token: tokenDropi,
        is_active: true,
      });

      setModalOpenDropi(false);
      setTokenDropi("");
      showSuccess("dropi");
    } catch (error: any) {
      toast.error(dictionary["integration-error"][language]);
    }
  };

  const handlePostMercadoPagoIntegration = async () => {
    const id = registerSellerList.MERCADO_PAGO;
    setLoadingMercadoPago(true);
    try {
      await postRegisterSellerApi(id, {
        public_key: tokenMercadoPagoPublic,
        token: tokenMercadoPagoPrivate,
      });

      await updatePaymentMethodActive("mercadopago");

      setModalOpenMercadoPago(false);
      setTokenMercadoPagoPublic("");
      setTokenMercadoPagoPrivate("");
      showSuccess("mercadopago");
    } catch (error: any) {
      const trigger = error?.response?.data?.trigger;
      const message = trigger && dictionary[trigger]?.[language]
        ? dictionary[trigger][language]
        : dictionary["integration-error"][language];
      toast.error(message, { duration: 5000 });
    } finally {
      setLoadingMercadoPago(false);
    }
  };

  const handlePostStripeIntegration = async (alreadyConfigured: boolean = false) => {
    setLoadingCard(true);
    if (alreadyConfigured) {
      const {res, error} = await handleApi(updatePaymentMethodActive(registerSellerList.STRIPE), false);
      if (error) {
        toast.error(dictionary["integration-error"][language]);
      }
      if (res) {
        showSuccess("stripe");
      }
      setLoadingCard(false);
      return;
    }
    const { res, error } = await handleApi(
      postRegisterSellerApi(registerSellerList.STRIPE, {}),
      false
    );
    if (error) {
      toast.error(dictionary["integration-error"][language]);
    }
    if (res) {
      if (res.config.onboarding_url) {
        window.open(res.config.onboarding_url, "_blank");
      }
      showSuccess("stripe");
    }
    setLoadingCard(false);
  };

  const handleWhatsAppOAuthComplete = async (data: EmbeddedSignupResult) => {
    setLoadingWhatsApp(true);
    try {
      const ownerId = decodeTokenService.getUserIdFromToken();
      if (!ownerId) throw new Error("No owner_id found");
      await whatsappOAuthCallback({
        code: data.code,
        phone_number_id: data.phone_number_id,
        waba_id: data.waba_id,
        owner_id: ownerId,
      });
      setModalOpenWhatsApp(false);
      showSuccess("whatsapp");
    } catch (error: any) {
      toast.error(dictionary["integration-error"][language]);
    } finally {
      setLoadingWhatsApp(false);
    }
  };

  const handleMetaOAuthComplete = () => {
    setLoadingMeta(false);
    setModalOpenMeta(false);
    showSuccess("meta");
  };

  // TODO SKELETON?
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader />
      </div>
    );
  }

  return (
    <div className="bg-[#F5F6FA] px-4 md:p-8 md:ml-4">
      <h1 className="text-xl md:text-2xl font-bold mb-6">
        {dictionary["integrations"][language]}
      </h1>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-6 items-start">
        {integrations.map((integration) => (
          <div key={integration.type} className="flex justify-center w-full">
            <IntegrationsCard
              name={integration.name}
              logoSrc={integration.logoSrc}
              bgColor={integration.bgColor}
              isActive={integration.isActive}
              isConnected={integration.isConnected}
              onConnect={() => handleConnect(integration.type)}
              onCancel={() => handleCancel(integration.type)}
              loading={loadingCard}
              type={integration.type}
            />
          </div>
        ))}
      </div>

      {modalOpenDropi && (
        <ModalDropiIntegration
          setModalOpenDropi={setModalOpenDropi}
          handlePostDropiIntegration={handlePostDropiIntegration}
          token={tokenDropi}
          setToken={setTokenDropi}
          language={language}
        />
      )}

      {modalOpenMercadoPago && (
        <ModalMercadoPagoIntegration
          id="mercadopago"
          setModalOpenMercadoPago={setModalOpenMercadoPago}
          handlePostMercadoPagoIntegration={handlePostMercadoPagoIntegration}
          tokenPublic={tokenMercadoPagoPublic}
          setTokenPublic={setTokenMercadoPagoPublic}
          tokenPrivate={tokenMercadoPagoPrivate}
          setTokenPrivate={setTokenMercadoPagoPrivate}
          loading={loadingMercadoPago}
          language={language}
        />
      )}

      {modalOpenWhatsApp && (
        <ModalWhatsAppIntegration
          setModalOpen={setModalOpenWhatsApp}
          handleOAuthComplete={handleWhatsAppOAuthComplete}
          loading={loadingWhatsApp}
          language={language}
        />
      )}

      {modalOpenMeta && (
        <ModalMetaIntegration
          setModalOpen={setModalOpenMeta}
          handleOAuthComplete={handleMetaOAuthComplete}
          loading={loadingMeta}
          language={language}
        />
      )}

      <ModalSuccessIntegration
        isOpen={successModal.open}
        onClose={handleSuccessClose}
        integrationName={successModal.type ? (INTEGRATIONS_UI_CONFIG[successModal.type as keyof typeof INTEGRATIONS_UI_CONFIG]?.name ?? "") : ""}
        integrationLogo={successModal.type ? (INTEGRATIONS_UI_CONFIG[successModal.type as keyof typeof INTEGRATIONS_UI_CONFIG]?.logoSrc ?? "") : ""}
        language={language}
      />
    </div>
  );
};

export default IntegrationsPanel;
